import { CACHE_REFETCH_INTERVAL, CALENDAR_SLOTS_CACHE_STALE_TIME } from 'app.constants'
import { CalendarSlot, CalendarSlotApiResponse }  from 'old/types'
import { getIsoStringYearMonthDay, useMutationHandler, useQueryHandler, getShopId }  from 'old/utils'

//slots:
export const useCalendarSlots = (
    teamMemberIds: string[],
    date: Date,
    enabled: boolean
) => {

    //shop id:
    const shopId = getShopId()

    //date:
    const isoStringYearMonthDay = getIsoStringYearMonthDay(date)

    //query handler:
    return useQueryHandler<CalendarSlotApiResponse>({
        query: `
            query getCalendarBlockSlots (

                $request: GetCalendarBlockSlotsRequest!

            ) {
                getCalendarBlockSlots (

                    request: $request

                ) {

                    date
                    now

                    teamMembers{

                        id
                        name
                        
                        slots {

                            dateTime
                            duration
                            variantTitle

                            service {
                                id
                                title
                                groupAppointments
                                groupAppointmentSlots
                            }

                            items {

                                id
                                type

                                customer {
                                    id
                                    name
                                    email
                                    image {
                                        id
                                        path
                                    }
                                    phone
                                    billingPhone
                                }

                            }

                        }

                    }

                }
            }
        `,
        variables: {
            request: {
                shop: shopId,
                date: isoStringYearMonthDay,
                teamMembers: teamMemberIds
            }
        },
        cacheKey: ['calendarSlots', isoStringYearMonthDay, teamMemberIds, shopId],
        onSuccess: json => {
            const response: CalendarSlotApiResponse = json.data.getCalendarBlockSlots
            return {
                ...response,
                teamMembers: response.teamMembers.map(teamMember => {
                    return {
                        ...teamMember,
                        slots: sortSlots(teamMember.slots)
                    }
                })
            }
        },
        enabled,
        staleTime: CALENDAR_SLOTS_CACHE_STALE_TIME,
        refetchInterval: CACHE_REFETCH_INTERVAL,
        keepPreviousData: true
    })

}

//sortSlots:
const sortSlots = (slots: CalendarSlot[]) => {

    const compare = (a: CalendarSlot, b: CalendarSlot) => {
        if(new Date(a.dateTime).getTime() < new Date(b.dateTime).getTime()){
          return -1
        }
        if(new Date(a.dateTime).getTime() > new Date(b.dateTime).getTime()){
          return 1
        }
        return 0
    }

    return slots.sort(compare)

}

//check in:
export const useAppointmentCheckIn = (appointmentId: string) => useMutationHandler<boolean>({
    query: `
        mutation checkedInAppointment(
            $shop: String!,
            $id: String!,
            $note: String!
        ){
            checkedInAppointment(
                shop: $shop,
                id: $id,
                note: $note
            ){
                id
            }
        }
    `,
    variables: {
        shop: getShopId(),
        id: appointmentId,
        note: ''
    },
    onSuccess: json => json.data.checkedInAppointment.id === appointmentId
})

//resend email:
export const useAppointmentResendConfirmEmail = (appointmentId: string) => useMutationHandler<boolean>({
    query: `
        mutation resendAppointmentConfirmation (
            $id: String!,
            $shop: String!
        ) {
            resendAppointmentConfirmation (
                id: $id,
                shop: $shop
            ){
                message
            }
        }
    `,
    variables: {
        id: appointmentId,
        shop: getShopId()
    },
    onSuccess: json => json.data.resendAppointmentConfirmation.message === 'Done'
})

//no show:
export const useAppointmentNoShow = (appointmentId: string) => useMutationHandler<boolean>({
    query: `
        mutation noShowAppointment (

            $id: String!,
            $shop: String!,
            $note: String!

        ) {
            noShowAppointment (

                id: $id,
                shop: $shop,
                note: $note

            ){
                id
            }
        }
    `,
    variables: {
        id: appointmentId,
        shop: getShopId(),
        note: ''
    },
    onSuccess: json => json.data.noShowAppointment.id === appointmentId
})

export const useAppointmentCancel = (appointmentId: string, refund: boolean) => useMutationHandler<boolean>({
    query: `
        mutation cancelAppointment (

            $id: String!,
            $shop: String!,
            $request: CancelAppointmentRequest!

        ) {
            cancelAppointment (

                id: $id,
                shop: $shop,
                request: $request

            ){
                message
            }
        }
    `,
    variables: {
        id: appointmentId,
        shop: getShopId(),
        request: {
            refundMoney: refund,
            content: null
        }
    },
    onSuccess: json => json.data.cancelAppointment.message === 'Done'
})
